/**
 * Hook into Alpine so that it is available no matter at which point Alpine loads.
 */
document.addEventListener('alpine:init', () => {
    /**
     * Adds a token Alpine data object to manage the token authentication.
     */
    Alpine.data('token', () => ({
        /**
         * @var string true when the validation is processing
         */
        validating: false,

        /**
         * @var int maximum length of the code
         */
        length: 0,

        /**
         * @var string method name to call on the livewire side when validating codes
         */
        method: '',

        /**
         * @var bool numeric input flag
         */
        numeric: false,

        /**
         * Initiate the token plugin so that we know its parameters.
         *
         * @param int    length the maximum length of the code
         * @param string method name to call on the livewire side when validating codes
         * @param bool   numeric input flag
         *
         * @return void
         */
        initToken(length, method, numeric = false) {
            this.length = length;
            this.method = method;
            this.numeric = numeric;
        },

        /**
         * Initiate the input so that it always resets on page load (no browser pre-fill).
         *
         * @param int index the index of the element
         *
         * @return void
         */
        initInput(index) {
            this.$refs[index].value = '';
            if (!index) {
                this.$refs[index].focus();
            }
        },

        /**
         * Retrieve the code by assembling it from all of the input values.
         *
         * @return string
         */
        getCode() {
            return Array.from(Array(this.length), (element, i) => {
                return this.$refs[i].value || '';
            }).join('');
        },

        /**
         * Reset the code by resetting all of the inputs and targeting the first input.
         *
         * @return void
         */
        resetCode() {
            Array.from(Array(this.length)).forEach((element, i) => {
                this.$refs[i].value = '';
            });
        },

        /**
         * Handle the event when a user types into an input.
         *
         * @param event event the event that triggered the input
         *
         * @return void
         */
        handleInput(event) {
            const input = event.target;
            const filter = RegExp(this.numeric ? /[0-9]+/ : /[a-zA-Z0-9]/);
            if (!filter.test(input.value)) {
                input.value = '';
            } else {
                input.value = input.value.toUpperCase();
                if (input.nextElementSibling) {
                    input.nextElementSibling.focus();
                    input.nextElementSibling.select();
                } else {
                    this.validateCode();
                    this.$refs[0].focus();
                }
            }
        },

        /**
         * Handle the event when the user pastes into an input.
         *
         * @param {ClipboardEvent} event event the event that triggered the input
         *
         * @return void
         */
        handlePaste(event) {
            let paste = event.clipboardData.getData('text');
            const filter = this.numeric ? /[^0-9]+/g : /[^a-zA-Z0-9]/g;
            paste = paste.replace(filter, '').toUpperCase();
            let input = event.target;
            input.value = paste[0];
            for (let i = 1; i < paste.length; i++) {
                if (input.nextElementSibling) {
                    input = input.nextElementSibling;
                    input.value = paste[i];
                    if (input.nextElementSibling) {
                        input.nextElementSibling.focus();
                        input.nextElementSibling.select();
                    } else {
                        this.validateCode();
                        this.$refs[0].focus();
                        break;
                    }
                }
            }
        },

        /**
         * Handle the event when the user backspaces an input.
         *
         * @param event event the event that triggered the input
         *
         * @return void
         */
        handleBackspace(event) {
            const input = event.target;
            if (input.previousElementSibling) {
                input.previousElementSibling.value = '';
                input.previousElementSibling.focus();
                input.previousElementSibling.select();
            }
        },

        /**
         * Handle the event whenever the user focuses into an input.
         *
         * @param event event the event that triggered the input
         *
         * @return void
         */
        handleFocus(event) {
            if (!this.validating) {
                const input = event.target;
                if (input.previousElementSibling && (input.previousElementSibling.value === '')) {
                    input.previousElementSibling.focus();
                    input.previousElementSibling.select();
                } else if ((input.value !== '') && input.nextElementSibling) {
                    input.nextElementSibling.focus();
                    input.nextElementSibling.select();
                }
            }
        },

        /**
         * Validate the code any time it changes when it has reached maximum length.
         *
         * @return void
         */
        validateCode() {
            const code = this.getCode();
            if (!this.validating && (code.length >= this.length)) {
                this.validating = true;
                this.$wire[this.method](code).then(function(valid) {
                    if (valid === false) {
                        this.$refs.container.classList.add('token-invalid');
                        setTimeout(function() {
                            this.$refs.container.classList.remove('token-invalid');
                            this.validating = false;
                            this.resetCode();
                        }.bind(this), 750);
                    } else if (valid === true) {
                        this.validating = false;
                        this.resetCode();
                    } else {
                        this.$refs.container.classList.add('token-valid');
                    }
                }.bind(this));
            }
        },
    }));
});
